import { memo, useEffect, useRef } from 'react';
import { lemmaSurfaceRegex } from '../../../lib/lemmaPrefix';

/**
 * Renders a chapter's full HTML with graduated emphasis around the target word.
 * The target word gets a blue pulse glow; nearby sentences are normal; distant
 * text fades.
 *
 * Props:
 *   fullHtml       — trusted ingested chapter HTML
 *   targetWord     — the vocabulary lemma to highlight
 *   targetSentence — the sentence containing the target (used to pick the right occurrence)
 *   bookTitle, chapterTitle — attribution shown below the reader
 *
 * Wrapped in React.memo so parent state changes (e.g. the student typing in
 * the question input) do NOT re-render this component and wipe out the DOM
 * mutations applied in useEffect.
 */
function ChapterContextReader({
  fullHtml,
  targetWord,
  targetSentence,
  bookTitle,
  chapterTitle,
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !fullHtml) return;

    // Set innerHTML directly (not via dangerouslySetInnerHTML) so React
    // doesn't re-apply it on re-renders and wipe our mutations.
    container.innerHTML = fullHtml;

    // Use the shared lemma-prefix helper so inflections like "reminisce"
    // → "reminiscing" / "hurtle" → "hurtling" still highlight (§3.3).
    const wordRegex = lemmaSurfaceRegex(targetWord);

    // Collect all text nodes, then pick the one most likely inside the target sentence
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    let targetNode = null;
    let targetMatch = null;

    for (const node of textNodes) {
      const match = node.textContent.match(wordRegex);
      if (!match) continue;
      const parentText = node.parentElement?.textContent || '';
      if (targetSentence && parentText.includes(targetSentence.slice(0, 30))) {
        targetNode = node;
        targetMatch = match;
        break;
      }
      if (!targetNode) {
        targetNode = node;
        targetMatch = match;
      }
    }

    if (targetNode && targetMatch) {
      const range = document.createRange();
      range.setStart(targetNode, targetMatch.index);
      range.setEnd(targetNode, targetMatch.index + targetMatch[0].length);
      const strong = document.createElement('strong');
      strong.className = 'vocab-context-target';
      range.surroundContents(strong);
      strong.scrollIntoView({ behavior: 'instant', block: 'center' });
    }

    // Graduated dimming on block-level elements based on distance from the target
    const blocks = container.querySelectorAll('p, blockquote, li, div.paragraph');
    let targetBlockIndex = -1;
    for (let i = 0; i < blocks.length; i++) {
      if (blocks[i].querySelector('.vocab-context-target')) {
        targetBlockIndex = i;
        break;
      }
    }

    if (targetBlockIndex !== -1) {
      blocks.forEach((block, i) => {
        const distance = Math.abs(i - targetBlockIndex);
        if (distance === 0) {
          block.classList.add('vocab-context-emphasized');
        } else if (distance <= 2) {
          block.classList.add('vocab-context-nearby');
        } else {
          block.classList.add('vocab-context-distant');
        }
      });
    }
  }, [fullHtml, targetWord, targetSentence]);

  return (
    <>
      <div className="vocab-chapter-reader" data-live-scroll>
        <div ref={containerRef} className="vocab-chapter-content" />
      </div>
      {/* Attribution lives OUTSIDE the scrolling reader so the user
          doesn't have to scroll to the bottom of a long chapter to see
          which book/chapter it's from. Book title italicized per
          typography convention. */}
      {(bookTitle || chapterTitle) && (
        <div className="vocab-chapter-attribution">
          {bookTitle && <em>{bookTitle}</em>}
          {bookTitle && chapterTitle && ' — '}
          {chapterTitle}
        </div>
      )}
    </>
  );
}

// Memoize: parent re-renders on every keystroke in the question input.
// The reader's props are stable, so we skip the re-render entirely.
export default memo(ChapterContextReader);
